import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CreateUserRequestDTO, EditUserRequestDTO, FindAllUsersResponseDTO, UserDTO, UserFiltersDTO } from "./dto";
import { NewUserService } from "./service";
import { PromiseStatuses } from "../../lib/utilis";

interface UserState {
    findAllUserStatus: PromiseStatuses
    findAllUserResponse?: FindAllUsersResponseDTO
    userFilters: UserFiltersDTO
    deleteUserStatus: PromiseStatuses
    createUserRequest: CreateUserRequestDTO
    createUserStatus: PromiseStatuses
    findByIdUserStatus: PromiseStatuses
    findByIdUserResponse?: UserDTO
    editUserStatus: PromiseStatuses
    editUserRequest: EditUserRequestDTO
    openNewUserModal: boolean
    selectedTab: number
    openConfirmModal: boolean
    userId: string
}

const initialState: UserState = {
    findAllUserStatus: 'idle',
    userFilters: { page: 0, itemsPerPage: 25, name: '', sort: '', order: false },
    deleteUserStatus: 'idle',
    createUserRequest: {
        name: "",
        surname: "",
        email: "",
        role: null,
        username: "",
        company: ""
    },
    createUserStatus: 'idle',
    findByIdUserStatus: 'idle',
    editUserRequest: {
        name: "",
        surname: "",
        username: "",
        company: ""
    },
    editUserStatus: 'idle',
    openNewUserModal: false,
    selectedTab: 0,
    openConfirmModal: false,
    userId: ""
}

export const findAllUser = createAsyncThunk(
    'User/findAllUser',
    async (filters: UserFiltersDTO, thunkApi): Promise<FindAllUsersResponseDTO> => {
        const UserService = NewUserService()

        return UserService.findAllUser(filters)
    }
)

export const createUser = createAsyncThunk(
    'User/createUser',
    async (request: CreateUserRequestDTO, thunkApi): Promise<void> => {
        const UserService = NewUserService()

        return UserService.createUser(request)
    }
)

export const findByIdUser = createAsyncThunk(
    'User/findByIdUser',
    async (id: string, thunkApi): Promise<UserDTO> => {
        const UserService = NewUserService()

        return UserService.findByIdUser(id)
    }
)

export const editUser = createAsyncThunk(
    'User/editUser',
    async (request: { id: string, request: EditUserRequestDTO }, thunkApi): Promise<void> => {
        const UserService = NewUserService()

        return UserService.editUser(request.id, request.request)
    }
)

export const deleteUser = createAsyncThunk(
    'User/deleteUser',
    async (id: string, thunkApi): Promise<void> => {
        const UserService = NewUserService()

        return UserService.deleteUser(id)
    }
)

const userSlice = createSlice({
    name: 'User/slice',
    initialState,
    extraReducers(builder) {
        builder
            .addCase(findAllUser.pending, (state) => {
                state.findAllUserStatus = 'loading'
            })
            .addCase(findAllUser.fulfilled, (state, action) => {
                state.findAllUserStatus = 'successfully'
                state.findAllUserResponse = action.payload
            })
            .addCase(findAllUser.rejected, (state) => {
                state.findAllUserStatus = 'failed'
            })
            .addCase(createUser.pending, (state) => {
                state.createUserStatus = 'loading'
            })
            .addCase(createUser.fulfilled, (state, action) => {
                state.createUserStatus = 'successfully'
            })
            .addCase(createUser.rejected, (state) => {
                state.createUserStatus = 'failed'
            })
            .addCase(findByIdUser.pending, (state) => {
                state.findByIdUserStatus = 'loading'
            })
            .addCase(findByIdUser.fulfilled, (state, action) => {
                state.findByIdUserStatus = 'successfully'
                state.findByIdUserResponse = action.payload
            })
            .addCase(findByIdUser.rejected, (state) => {
                state.findByIdUserStatus = 'failed'
            })
            .addCase(editUser.pending, (state) => {
                state.editUserStatus = 'loading'
            })
            .addCase(editUser.fulfilled, (state, action) => {
                state.editUserStatus = 'successfully'
            })
            .addCase(editUser.rejected, (state) => {
                state.editUserStatus = 'failed'
            })
            .addCase(deleteUser.pending, (state) => {
                state.deleteUserStatus = 'loading'
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                state.deleteUserStatus = 'successfully'
            })
            .addCase(deleteUser.rejected, (state) => {
                state.deleteUserStatus = 'failed'
            })
    },
    reducers: {
        setFindAllUserStatus: (state, action) => {
            state.findAllUserStatus = action.payload
        },
        setFindByIdUserStatus: (state, action) => {
            state.findByIdUserStatus = action.payload
        },
        resetCreateUserRequest: (state) => {
            state.createUserRequest = {
                name: "",
                surname: "",
                email: "",
                role: null,
                username: "",
                company: ""
            }
        },
        setCreateUserStatus: (state, action) => {
            state.createUserStatus = action.payload
        },
        setCreateUserRequestName: (state, action) => {
            state.createUserRequest.name = action.payload
        },
        setCreateUserRequestSurname: (state, action) => {
            state.createUserRequest.surname = action.payload
        },
        setCreateUserRequestUsername: (state, action) => {
            state.createUserRequest.username = action.payload
        },
        setCreateUserRequestEmail: (state, action) => {
            state.createUserRequest.email = action.payload
        },
        setCreateUserRequestCompany: (state, action) => {
            state.createUserRequest.company = action.payload
        },
        setCreateUserRequestRole: (state, action) => {
            state.createUserRequest.role = action.payload
        },
        setEditUserStatus: (state, action) => {
            state.editUserStatus = action.payload
        },
        setEditUserRequestName: (state, action) => {
            state.editUserRequest.name = action.payload
        },
        setEditUserRequestSurname: (state, action) => {
            state.editUserRequest.surname = action.payload
        },
        setEditUserRequestUsername: (state, action) => {
            state.editUserRequest.username = action.payload
        },
        setEditUserRequestCompany: (state, action) => {
            state.editUserRequest.company = action.payload
        },
        setDeleteUserStatus: (state, action) => {
            state.deleteUserStatus = action.payload
        },
        setOpenNewUserModal: (state, action) => {
            state.openNewUserModal = action.payload
        },
        setUserFilterName: (state, action) => {
            state.userFilters.name = action.payload
        },
        setSelectedTab: (state, action) => {
            state.selectedTab = action.payload
        },
        setOpenConfirmModal: (state, action) => {
            state.openConfirmModal = action.payload
        },
        setUserFilterPage: (state, action) => {
            state.userFilters.page = action.payload
        },
        setUserId: (state, action) => {
            state.userId = action.payload
        },
    },
})

export const {
    setCreateUserRequestEmail,
    setCreateUserRequestName,
    setCreateUserRequestSurname,
    setCreateUserRequestUsername,
    setCreateUserRequestCompany,
    setCreateUserStatus,
    setDeleteUserStatus,
    setEditUserRequestName,
    setEditUserRequestSurname,
    setEditUserRequestUsername,
    setEditUserRequestCompany,
    setEditUserStatus,
    setFindAllUserStatus,
    setFindByIdUserStatus,
    setOpenNewUserModal,
    setUserFilterName,
    setSelectedTab,
    setOpenConfirmModal,
    setUserFilterPage,
    setUserId,
    resetCreateUserRequest,
    setCreateUserRequestRole,
} = userSlice.actions

export default userSlice.reducer

